import { useEffect, useState } from "react";
import { Path, CaretRight, MapPin, CheckCircle, ArrowsClockwise, Check, Camera, X, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import { routesApi, jobsApi } from "@/lib/api";

/* One-shot, high-accuracy phone GPS read. Resolves null on denial/failure so
 * the driver can still mark a stop complete (we just won't stamp it). */
function getOneShotGPS({ timeoutMs = 10000 } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    try {
      navigator.geolocation.getCurrentPosition(
        (pos) => done({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          captured_at: new Date(pos.timestamp || Date.now()).toISOString(),
        }),
        () => done(null),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: timeoutMs },
      );
    } catch (_e) {
      done(null);
    }
  });
}

export default function TodayRouteCard() {
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null); // { seq, job_id, file? }

  const load = async () => {
    try {
      const r = await routesApi.myToday();
      setRoute(r || null);
    } catch (e) {
      console.warn("[TodayRouteCard] load failed", e);
      setRoute(null);
    } finally {
      setLoading(false);
    }
  };

  const handleStopTap = async (stop) => {
    if (stop.completed_at) return;
    if (stop.job_id) {
      // Open the "complete with optional POD photo" sheet
      setCompleting({ seq: stop.seq, job_id: stop.job_id, customer: stop.customer || stop.label });
    } else {
      // Free-form stops don't have a job to attach photos to — capture GPS then complete
      const gps = await getOneShotGPS();
      doComplete(stop.seq, null, gps);
    }
  };

  const doComplete = async (seq, photoId, gps) => {
    try {
      const updated = await routesApi.completeStop(route.id, seq, photoId, gps);
      setRoute(updated);
      const stamped = gps && gps.lat != null ? " · GPS-stamped" : "";
      toast.success(`Stop ${seq} complete${photoId ? " · POD" : ""}${stamped}`);
      setCompleting(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't mark complete");
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, []);

  if (loading || !route) return null;

  const completed = route.stops.filter((s) => s.completed_at).length;
  const pct = route.stops.length ? Math.round((completed / route.stops.length) * 100) : 0;
  const allDone = completed === route.stops.length;

  return (
    <>
      <div className="bg-[#0d0d0d] border-2 border-[#ff8c00] mb-5" data-testid="today-route-card">
        <div className="px-4 py-3 bg-[#ff8c00]/10 border-b border-[#ff8c00]/40 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Path size={16} className="text-[#ff8c00]" weight="bold" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">Today's Route</div>
              <div className="font-heading font-black text-base tracking-tight">{route.name}</div>
            </div>
          </div>
          <button onClick={load} className="text-[#ff8c00] hover:bg-[#ff8c00]/20 p-1" title="Refresh" data-testid="today-route-refresh">
            <ArrowsClockwise size={11} weight="bold" />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-[#2a2a2a]">
          <div className="flex items-center justify-between text-[11px] font-mono mb-1.5">
            <span className="text-[#a1a1aa]">{route.stops.length} stops · {route.total_miles} mi</span>
            <span className="text-[#22c55e] font-bold">
              {allDone ? "✓ COMPLETE" : `${completed}/${route.stops.length} done`}
            </span>
          </div>
          <div className="h-1.5 bg-[#1a1a1a] overflow-hidden" data-testid="today-route-progress">
            <div className="h-full bg-[#22c55e] transition-all duration-500" style={{ width: `${pct}%` }} />
          </div>
        </div>

        <div className="divide-y divide-[#2a2a2a]">
          {route.stops.map((s) => {
            const isDone = !!s.completed_at;
            const hasGps = isDone && s.completed_lat != null && s.completed_lng != null;
            return (
              <div key={s.seq} className="px-4 py-3 flex items-center gap-3" data-testid={`today-stop-${s.seq}`}>
                <button
                  type="button"
                  onClick={() => handleStopTap(s)}
                  disabled={isDone}
                  className={`w-11 h-11 flex items-center justify-center transition-colors font-mono font-black ${
                    isDone ? "bg-[#22c55e] text-black cursor-default" : "bg-[#ff8c00] text-black hover:bg-[#22c55e]/80 active:bg-[#22c55e]"
                  }`}
                  title={isDone ? `Completed at ${new Date(s.completed_at).toLocaleTimeString()}` : "Tap to mark complete"}
                  data-testid={`today-stop-complete-${s.seq}`}
                >
                  {isDone ? <CheckCircle size={20} weight="fill" /> : s.seq}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm flex items-center gap-2 truncate">
                    {s.job_number ? <span className="text-[#ff8c00] font-mono text-xs">{s.job_number}</span> : null}
                    <span className={`truncate ${isDone ? "text-[#71717a] line-through" : ""}`}>{s.customer || s.label}</span>
                    {s.proof_photo_id && <Camera size={12} className="text-[#22c55e] shrink-0" weight="fill" title="POD attached" />}
                    {hasGps && <Crosshair size={12} className="text-[#22c55e] shrink-0" weight="bold" title="GPS-stamped" data-testid={`today-stop-gps-${s.seq}`} />}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono flex items-center gap-1 mt-0.5">
                    <MapPin size={9} />{s.lat.toFixed(4)}, {s.lng.toFixed(4)}
                    {s.weight_lbs ? ` · ${(s.weight_lbs / 1000).toFixed(1)}k lbs` : ""}
                    {isDone && s.completed_at ? ` · ✓ ${new Date(s.completed_at).toLocaleTimeString()}` : ""}
                  </div>
                  {hasGps && (
                    <a
                      href={`https://maps.google.com/?q=${s.completed_lat},${s.completed_lng}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-[10px] text-[#22c55e] hover:text-[#22c55e]/80 hover:underline font-mono flex items-center gap-1 mt-0.5 w-fit"
                      data-testid={`today-stop-gps-link-${s.seq}`}
                      title="Open completion coords on Google Maps"
                    >
                      <Crosshair size={9} weight="bold" />
                      stamp {s.completed_lat.toFixed(5)}, {s.completed_lng.toFixed(5)}
                      {s.completed_accuracy_m != null ? ` · ±${Math.round(s.completed_accuracy_m)} m` : ""}
                    </a>
                  )}
                </div>
                <a
                  href={`https://maps.google.com/?q=${s.lat},${s.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#4682b4] hover:bg-[#4682b4]/10 p-2 flex items-center"
                  data-testid={`today-stop-nav-${s.seq}`}
                  title="Navigate"
                >
                  <CaretRight size={14} weight="bold" />
                </a>
              </div>
            );
          })}
        </div>
        {allDone && (
          <div className="px-4 py-3 bg-[#22c55e]/10 border-t border-[#22c55e]/40 text-center" data-testid="today-route-complete-banner">
            <div className="text-[#22c55e] font-bold uppercase tracking-[0.25em] text-xs flex items-center justify-center gap-2">
              <Check size={14} weight="bold" /> Route complete — nice work
            </div>
          </div>
        )}
      </div>

      {completing && (
        <CompleteStopSheet
          stop={completing}
          onClose={() => setCompleting(null)}
          onDone={(photoId, gps) => doComplete(completing.seq, photoId, gps)}
        />
      )}
    </>
  );
}

function CompleteStopSheet({ stop, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gps, setGps] = useState(null);
  const [gpsState, setGpsState] = useState("idle"); // idle | locating | ok | denied

  // Lock in a one-shot GPS fix as soon as the sheet opens. The driver shouldn't
  // have to wait or tap anything extra — the prompt fires in parallel with the
  // photo capture.
  useEffect(() => {
    let cancelled = false;
    setGpsState("locating");
    getOneShotGPS().then((g) => {
      if (cancelled) return;
      if (g) { setGps(g); setGpsState("ok"); }
      else { setGpsState("denied"); }
    });
    return () => { cancelled = true; };
  }, []);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const refreshGps = async () => {
    setGpsState("locating");
    const g = await getOneShotGPS();
    if (g) { setGps(g); setGpsState("ok"); } else { setGpsState("denied"); }
  };

  const submit = async (withPhoto) => {
    setBusy(true);
    try {
      let photoId = null;
      if (withPhoto && file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", "dropoff");
        fd.append("note", "Proof of delivery (POD/BOL)");
        if (gps && gps.lat != null) {
          fd.append("lat", String(gps.lat));
          fd.append("lng", String(gps.lng));
          if (gps.accuracy_m != null) fd.append("accuracy_m", String(gps.accuracy_m));
          if (gps.captured_at) fd.append("captured_at", gps.captured_at);
        }
        const photo = await jobsApi.uploadPhoto(stop.job_id, fd);
        photoId = photo.id;
      }
      onDone(photoId, gps);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="complete-stop-sheet">
      <div className="bg-[#0d0d0d] border border-[#2a2a2a] w-full max-w-md max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#2a2a2a] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Complete Stop {stop.seq}</div>
            <h2 className="font-heading font-black text-lg tracking-tight truncate">{stop.customer}</h2>
          </div>
          <button onClick={onClose} className="text-[#71717a] hover:text-white p-1" data-testid="complete-stop-close">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-[12px] text-[#a1a1aa] leading-relaxed">
            Snap the signed BOL / Proof of Delivery and attach it now (optional), or just mark complete.
          </p>

          {/* GPS stamp status pill */}
          <div
            className={`flex items-center justify-between gap-2 px-3 py-2 border text-[10px] uppercase tracking-[0.2em] font-mono ${
              gpsState === "ok"
                ? "border-[#22c55e]/50 bg-[#22c55e]/10 text-[#22c55e]"
                : gpsState === "locating"
                ? "border-[#ff8c00]/50 bg-[#ff8c00]/10 text-[#ff8c00]"
                : "border-[#7f1d1d] bg-[#7f1d1d]/20 text-[#fca5a5]"
            }`}
            data-testid="complete-stop-gps-status"
          >
            <span className="flex items-center gap-2">
              <Crosshair size={11} weight="bold" />
              {gpsState === "ok" && gps
                ? `GPS ${gps.lat.toFixed(5)}, ${gps.lng.toFixed(5)} · ±${Math.round(gps.accuracy_m || 0)} m`
                : gpsState === "locating"
                ? "Locating phone GPS…"
                : "GPS unavailable — proof won't be stamped"}
            </span>
            <button
              type="button"
              onClick={refreshGps}
              className="text-current hover:opacity-80 p-1"
              title="Re-acquire GPS"
              data-testid="complete-stop-gps-retry"
            >
              <ArrowsClockwise size={11} weight="bold" />
            </button>
          </div>

          <label className="block border-2 border-dashed border-[#2a2a2a] hover:border-[#ff8c00] p-6 text-center cursor-pointer transition-colors" data-testid="complete-stop-photo-pick">
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={pick}
              className="hidden"
              data-testid="complete-stop-photo-input"
            />
            {!preview ? (
              <div>
                <Camera size={28} weight="bold" className="text-[#ff8c00] mx-auto" />
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#a1a1aa] font-bold mt-2">Tap to take POD photo</div>
                <div className="text-[10px] text-[#71717a] mt-1">Up to 8 MB · jpg/png/heic · auto GPS-watermarked</div>
              </div>
            ) : (
              <img src={preview} alt="POD preview" className="max-h-44 mx-auto object-contain" data-testid="complete-stop-photo-preview" />
            )}
          </label>

          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => submit(false)}
              disabled={busy}
              className="border border-[#2a2a2a] text-[#a1a1aa] hover:text-white px-3 py-3 text-[10px] uppercase tracking-[0.25em] font-bold flex items-center justify-center gap-2"
              data-testid="complete-stop-no-photo"
            >
              {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
              Skip & Complete
            </button>
            <button
              onClick={() => submit(true)}
              disabled={busy || !file}
              className="bg-[#22c55e] hover:bg-[#22c55e]/90 disabled:opacity-40 text-black font-bold py-3 text-[10px] uppercase tracking-[0.25em] flex items-center justify-center gap-2"
              data-testid="complete-stop-with-photo"
            >
              {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : <Check size={11} weight="bold" />}
              Upload & Complete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
